---
title: "Książka obiektu budowlanego — co musi zawierać i jak ją prowadzić"
description: "Książka obiektu budowlanego co musi zawierać — obowiązkowe elementy, zasady prowadzenia, różnica między KOB a c-KOB oraz jak Pinveo wspiera gromadzenie dokumentacji przeglądów."
date: "2026-06-07"
slug: "ksiazka-obiektu-budowlanego-co-zawiera"
faq:
  - q: "Czy KOB i c-KOB to to samo?"
    a: "Nie. KOB (książka obiektu budowlanego) to fizyczny dokument prowadzony przez właściciela lub zarządcę. c-KOB to rządowa platforma GUNB — cyfrowa wersja tej samej ewidencji, obowiązkowa dla budynków oddawanych do użytkowania od 2023 roku."
  - q: "Kto może wpisywać dane do KOB?"
    a: "Wpisy dotyczące przeglądów i kontroli dokonują wyłącznie osoby z uprawnieniami budowlanymi w odpowiedniej specjalności lub (w zakresie kominiarskim) mistrzowie kominiarskiej. Właściciel lub zarządca uzupełnia dane administracyjne i ewidencyjne."
  - q: "Jak długo przechowuje się KOB?"
    a: "KOB jest prowadzona przez cały okres istnienia obiektu i powinna być przechowywana przez właściciela lub zarządcę. Protokoły z przeglądów należy przechowywać co najmniej 5 lat."
  - q: "Co grozi za brak KOB?"
    a: "Brak książki obiektu budowlanego lub jej nieprawidłowe prowadzenie może skutkować nałożeniem grzywny przez Powiatowego Inspektora Nadzoru Budowlanego (PINB) w trybie art. 93 pkt 9 Prawa budowlanego."
  - q: "Czy Pinveo zastępuje c-KOB?"
    a: "Nie. Pinveo to narzędzie do dokumentacji terenowej — wspiera inspekcje i przeglądy, ale nie jest platformą rządową ani nie spełnia wymogu prowadzenia c-KOB w systemie GUNB."
---

Książka obiektu budowlanego co musi zawierać — to pytanie, które zadaje sobie każdy właściciel i zarządca nieruchomości zobowiązany do jej prowadzenia. KOB to fundamentalny dokument w historii każdego obiektu, a jej brak lub niekompletność grozi poważnymi konsekwencjami prawnymi.

---

## Czym jest książka obiektu budowlanego?

**Książka obiektu budowlanego (KOB)** to dokument prowadzony przez cały okres użytkowania obiektu budowlanego, obejmujący wszelkie informacje i dane techniczne dotyczące jego stanu technicznego, przebudów, remontów, przeglądów i kontroli. Obowiązek jej prowadzenia wynika z art. 64 ustawy Prawo budowlane.

KOB towarzyszy obiektowi od momentu oddania do użytkowania aż do jego rozbiórki — stanowi ciągły zapis historii technicznej budynku i jest podstawowym narzędziem zarządzania stanem technicznym nieruchomości.

---

## Kto jest zobowiązany prowadzić KOB?

Obowiązek prowadzenia książki obiektu budowlanego spoczywa na **właścicielu lub zarządcy** obiektu. Dotyczy to:

- budynków mieszkalnych wielorodzinnych,
- budynków użyteczności publicznej,
- obiektów przemysłowych i magazynowych,
- budowli wymagających pozwolenia na użytkowanie.

Obowiązek **nie dotyczy** jednorodzinnych budynków mieszkalnych, obiektów tymczasowych ani obiektów budownictwa zagrodowego i letniskowego (chyba że przepisy szczególne stanowią inaczej).

Za brak KOB lub jej nieprawidłowe prowadzenie grozi grzywna nakładana przez organy nadzoru budowlanego.

---

## Co zawiera książka obiektu budowlanego?

### Dane identyfikacyjne obiektu

- adres i lokalizacja obiektu,
- numer działki ewidencyjnej i obrębu,
- rok budowy i rok oddania do użytkowania,
- dane właściciela lub zarządcy,
- numer pozwolenia na użytkowanie,
- powierzchnia zabudowy i całkowita, kubatura.

### Protokoły z przeglądów okresowych

Obowiązkowe przeglądy okresowe budynku wymagają rejestrowania w KOB wyników:

- **kontroli rocznych** — stanu technicznego elementów narażonych na wpływy atmosferyczne i eksploatację,
- **kontroli pięcioletnich** — pełnego stanu technicznego i przydatności do użytkowania,
- **kontroli przewodów kominowych** — co najmniej raz w roku przez mistrza kominiarskiego lub osobę z uprawnieniami budowlanymi,
- **doraźnych kontroli** — po zdarzeniach losowych (silny wiatr, intensywne opady, katastrofa).

### Dokumentacja robót budowlanych

- wpisy o remontach, przebudowach i modernizacjach (zakres, data, wykonawca),
- informacje o awariach i sposobie ich usunięcia,
- zmiany sposobu użytkowania obiektu lub jego części.

### Dane techniczne instalacji

- instalacje elektryczne i odgromowe,
- instalacje gazowe,
- instalacje wod-kan i centralnego ogrzewania,
- windy i urządzenia dźwigowe (z wpisami UDT),
- instalacje przeciwpożarowe.

### Wykaz dokumentacji

KOB zawiera spis dokumentów przechowywanych oddzielnie: projekt budowlany, decyzje administracyjne, protokoły odbiorów, świadectwa charakterystyki energetycznej.

---

## KOB papierowa a c-KOB — ważny disclaimer

Od **1 stycznia 2023 roku** działa **c-KOB** — Cyfrowa Książka Obiektu Budowlanego, prowadzona przez Główny Urząd Nadzoru Budowlanego (GUNB) na platformie e-budownictwo. Dla nowych budynków oddawanych do użytkowania po tej dacie prowadzenie c-KOB jest obowiązkowe.

**Pinveo NIE jest systemem c-KOB i nie zastępuje c-KOB.** c-KOB to rządowy system GUNB, do którego wprowadzać musi uprawniony zarządca lub właściciel. Pinveo jest narzędziem do dokumentacji terenowej — wspiera **gromadzenie i organizowanie materiałów z przeglądów** (protokoły, zdjęcia, formularze), które następnie właściciel lub zarządca wprowadza do c-KOB lub przechowuje w ramach papierowej KOB.

---

## Najczęstsze błędy przy prowadzeniu KOB

- **Niekompletne wpisy** — brak daty, nazwiska osoby uprawnionej lub zakresu przeprowadzonej kontroli.
- **Brak protokołów z przeglądów rocznych** — najczęstsza przyczyna uchybień stwierdzanych przez PINB.
- **Brak dokumentacji fotograficznej** — wpisy opisowe bez zdjęć trudno zweryfikować po czasie.
- **Zgubienie lub zniszczenie dokumentów** — KOB powinna być przechowywana w miejscu dostępnym, ale zabezpieczonym.
- **Nierejestrowanie drobnych awarii** — każde zdarzenie wpływające na stan techniczny powinno mieć wpis.
- **Prowadzenie tylko formy papierowej mimo obowiązku c-KOB** — dla budynków oddanych po 2023 roku.

---

## Jak Pinveo pomaga w dokumentacji przeglądów dla KOB?

Pinveo wspiera każdy etap zbierania materiałów, które trafiają do KOB lub c-KOB:

- **Formularze inspekcji terenowej** — gotowe szablony dla obowiązkowych kontroli rocznych i pięcioletnich z listą wszystkich wymaganych elementów.
- **Markery na rzucie PDF** — precyzyjne oznaczanie lokalizacji usterek bezpośrednio na planie kondygnacji lub elewacji budynku.
- **Zdjęcia z GPS i znacznikiem czasu** — automatyczna metadana gwarantuje wiarygodność dokumentacji fotograficznej.
- **Raport PDF i Excel** — gotowy protokół z przeglądu do wydruku lub przekazania zarządcy, który uzupełnia c-KOB.
- **Praca offline** — inspektor dokumentuje w terenie bez zasięgu, dane synchronizują się po powrocie do sieci.

Więcej o zastosowaniu Pinveo przy zarządzaniu nieruchomościami: [Zarządzanie nieruchomościami z Pinveo](/zastosowania/zarzadzanie-nieruchomosciami).

Zobacz też: [Przeglądy okresowe budynków z Pinveo](/zastosowania/przeglady-okresowe-budynkow).

---

## Podsumowanie

Książka obiektu budowlanego to dokument obowiązkowy, który musi zawierać pełną historię techniczną budynku — od danych identyfikacyjnych, przez protokoły przeglądów, po zapisy o remontach i awariach. Brak KOB lub jej nierzetelne prowadzenie to ryzyko prawne i administracyjne. Pinveo przyspiesza gromadzenie materiałów dokumentacyjnych z przeglądów, które zarządca wprowadza następnie do c-KOB lub archiwizuje w KOB.

---

## FAQ

### Czy KOB i c-KOB to to samo?

Nie. KOB (książka obiektu budowlanego) to fizyczny dokument prowadzony przez właściciela lub zarządcę. c-KOB to rządowa platforma GUNB — cyfrowa wersja tej samej ewidencji, obowiązkowa dla budynków oddawanych do użytkowania od 2023 roku.

### Kto może wpisywać dane do KOB?

Wpisy dotyczące przeglądów i kontroli dokonują wyłącznie osoby z uprawnieniami budowlanymi w odpowiedniej specjalności lub (w zakresie kominiarskim) mistrzowie kominiarskiej. Właściciel lub zarządca uzupełnia dane administracyjne i ewidencyjne.

### Jak długo przechowuje się KOB?

KOB jest prowadzona przez cały okres istnienia obiektu i powinna być przechowywana przez właściciela lub zarządcę. Protokoły z przeglądów należy przechowywać co najmniej 5 lat.

### Co grozi za brak KOB?

Brak książki obiektu budowlanego lub jej nieprawidłowe prowadzenie może skutkować nałożeniem grzywny przez Powiatowego Inspektora Nadzoru Budowlanego (PINB) w trybie art. 93 pkt 9 Prawa budowlanego.

### Czy Pinveo zastępuje c-KOB?

Nie. Pinveo to narzędzie do dokumentacji terenowej — wspiera inspekcje i przeglądy, ale nie jest platformą rządową ani nie spełnia wymogu prowadzenia c-KOB w systemie GUNB.
